import{
  attemptCaptureRpc,
  breedCreateEggRpc,
  breedHatchRpc,
  breedTickEggsRpc,
  listPlayerPokemon,
  migrateCollection,
}from'./collection-api.js';

const SYNC_INTERVAL_MS=60_000;
const QUEUE_VERSION=1;
const IV_MAP={hp:'HP',atk:'ATK',def:'DEF',spa:'ESP',spd:'RES',spe:'VEL'};
const IV_MARKET={HP:'hp',ATK:'atk',DEF:'def',ESP:'spa',RES:'spd',VEL:'spe'};

let userId='';
let migrated=false;
let hooks={
  getCollection:()=>[],
  applyCollection:()=>{},
  rarityColor:()=>'#a9b5ae',
  onMigrated:()=>{},
};
let timer=null;
let flushing=null;
let pendingMigrate=null;

const storageKey=()=>`idlemon-collection-sync:v${QUEUE_VERSION}:${userId}`;

function persist(){
  if(!userId)return;
  localStorage.setItem(storageKey(),JSON.stringify({
    version:QUEUE_VERSION,
    migrated,
    pendingMigrate,
  }));
}

function restore(){
  try{
    const saved=JSON.parse(localStorage.getItem(storageKey())||'{}');
    migrated=!!saved.migrated;
    pendingMigrate=saved.pendingMigrate?.requestId?saved.pendingMigrate:null;
  }catch{
    migrated=false;
    pendingMigrate=null;
  }
}

export function serverPokemonToLocal(row,helpers={}){
  if(!row)return null;
  const name=row.species||row.name;
  if(!name)return null;
  const ivs=Object.fromEntries(
    Object.entries(row.ivs||{}).map(([key,value])=>[IV_MAP[key]||key,Number(value)||0]),
  );
  const rarity=row.rarity||'Comum';
  const score=Math.max(0,Math.min(186,Number(row.iv_total)||0));
  const pct=Number(row.iv_total_pct);
  const level=Math.max(1,Number(row.level)||1);
  const pokemon={
    id:row.id,
    serverId:row.id,
    name,
    level,
    xp:Math.max(0,Number(row.xp)||0),
    encounterLevel:Math.max(1,Number(row.encounter_level)||level),
    capturedAt:row.captured_at?new Date(row.captured_at).getTime():Date.now(),
    ball:row.ball,
    rarity,
    mult:Number(row.mult)||1,
    color:helpers.rarityColor?.(rarity)||'#a9b5ae',
    ivs,
    ivScore:score,
    ivTotal:Number.isFinite(pct)?pct:Math.round(score/186*100),
    ivQuality:row.iv_quality||'regular',
    shiny:!!row.shiny,
    locked:!!row.locked,
    captureProtectionVersion:1,
    types:row.types||[],
    equippedMoves:Array.isArray(row.moves)?[...row.moves]:[],
    autoMoves:true,
    moveCursor:0,
    masteryLevel:Number(row.mastery_level)||0,
    masteryXp:Number(row.mastery_xp)||0,
    marketAssetId:row.custody_id||undefined,
    serverState:row.state,
    origin:row.origin,
    isActive:!!row.is_active,
    fingerprint:row.fingerprint||undefined,
    teamSlot:Number.isFinite(Number(row.team_slot))?Number(row.team_slot):null,
  };
  if(helpers.statsFor&&pokemon.types[0]){
    pokemon.stats=helpers.statsFor(name,pokemon.types[0],level,ivs,pokemon.mult);
  }
  return pokemon;
}

export function localPokemonToMigratePayload(pokemon){
  const ivs=pokemon.ivs||{};
  return{
    species:pokemon.name,
    name:pokemon.name,
    level:pokemon.level||1,
    encounter_level:pokemon.encounterLevel||pokemon.level||1,
    xp:pokemon.xp||0,
    ivs:Object.fromEntries(
      Object.entries(IV_MARKET).map(([clientKey,marketKey])=>[
        marketKey,
        Number(ivs[clientKey]??ivs[marketKey])||0,
      ]),
    ),
    rarity:pokemon.rarity||'Comum',
    mult:Number(pokemon.mult)||1,
    shiny:!!pokemon.shiny,
    ball:pokemon.ball,
    ball_slug:pokemon.ballSlug,
    moves:pokemon.equippedMoves||pokemon.moves||[],
    capturedAt:pokemon.capturedAt,
    captured_at:pokemon.capturedAt
      ?new Date(Number(pokemon.capturedAt)).toISOString()
      :undefined,
    locked:!!pokemon.locked,
  };
}

function applyServerList(list){
  const helpers={
    rarityColor:hooks.rarityColor,
    statsFor:hooks.statsFor,
  };
  const mapped=(list||[]).map(row=>serverPokemonToLocal(row,helpers)).filter(Boolean);
  hooks.applyCollection(mapped);
  return mapped;
}

export async function flushCollectionMigrate(){
  if(flushing||!userId||migrated)return flushing;
  flushing=(async()=>{
    try{
      if(!pendingMigrate){
        const local=hooks.getCollection()||[];
        pendingMigrate={
          requestId:crypto.randomUUID(),
          pokemon:local.map(localPokemonToMigratePayload),
        };
        persist();
      }
      const response=await migrateCollection(
        pendingMigrate.pokemon,
        pendingMigrate.requestId,
      );
      migrated=true;
      pendingMigrate=null;
      persist();
      if(Array.isArray(response?.pokemon))applyServerList(response.pokemon);
      hooks.onMigrated?.(response);
      return response;
    }catch(error){
      console.warn('collection migrate failed',error);
      throw error;
    }finally{
      flushing=null;
    }
  })();
  return flushing;
}

export async function refreshPlayerPokemon(){
  if(!userId)return[];
  const list=await listPlayerPokemon(['free','in_team']);
  return applyServerList(list);
}

export async function serverAttemptCapture(payload){
  if(!userId||!payload?.encounterId)return null;
  const requestId=crypto.randomUUID();
  const response=await attemptCaptureRpc(payload,requestId);
  if(response?.ok&&response.pokemon){
    const local=serverPokemonToLocal(response.pokemon,{
      rarityColor:hooks.rarityColor,
      statsFor:hooks.statsFor,
    });
    return{...response,local};
  }
  return response;
}

export async function serverBreedCreateEgg(payload){
  if(!userId)return null;
  return breedCreateEggRpc(payload,crypto.randomUUID());
}

export async function serverBreedHatch(payload){
  if(!userId||!payload?.eggId)return null;
  const requestId=crypto.randomUUID();
  const response=await breedHatchRpc(payload,requestId);
  if(response?.ok&&response.pokemon){
    const local=serverPokemonToLocal(response.pokemon,{
      rarityColor:hooks.rarityColor,
      statsFor:hooks.statsFor,
    });
    return{...response,local};
  }
  return response;
}

export async function serverBreedTickEggs(count=1){
  if(!userId)return null;
  return breedTickEggsRpc(count);
}

export function collectionSyncEnabled(){
  return!!userId&&migrated;
}

export async function initCollectionSync({
  account,
  getCollection,
  applyCollection,
  rarityColor,
  statsFor,
  onMigrated,
}={}){
  userId=account?.session?.user?.id||'';
  if(!userId)return null;
  hooks={
    getCollection:getCollection||hooks.getCollection,
    applyCollection:applyCollection||hooks.applyCollection,
    rarityColor:rarityColor||hooks.rarityColor,
    statsFor:statsFor||hooks.statsFor,
    onMigrated:onMigrated||hooks.onMigrated,
  };
  restore();
  if(account?.profile?.collection_migrated_at){
    migrated=true;
    pendingMigrate=null;
    persist();
  }
  clearInterval(timer);
  timer=setInterval(()=>{
    if(!migrated)flushCollectionMigrate().catch(()=>{});
    else refreshPlayerPokemon().catch(()=>{});
  },SYNC_INTERVAL_MS);
  addEventListener('visibilitychange',()=>{
    if(document.visibilityState==='hidden'&&!migrated){
      flushCollectionMigrate().catch(()=>{});
    }
  });

  if(!migrated){
    const result=await flushCollectionMigrate().catch(error=>({error}));
    return{migrated,result};
  }
  await refreshPlayerPokemon().catch(()=>{});
  return{migrated:true};
}
